import * as React from "react";
import { cn, getInitialsFromDisplayName } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useState } from "react";
import {
  OrganizationUsersQuery,
  User,
  useOrganizationUsersQuery,
} from "@common/utils/graphql";
import { Check, Plus, X } from "@phosphor-icons/react";
import { Body } from "@hypercard/web-components";
import { ExtractTypename } from "@common/utils/graphql/utils";

interface Employee {
  workEmail: string;
  name: string;
  userId: string;
  employeeProfileId: string;
}

interface Props {
  placeholder: string;
  onChange: (e: Employee) => void;
  defaultValue?: Pick<User, "displayName">;
  disabled?: boolean;
  exceptUserId?: string;
}
type OrganizationUsersResponse = ExtractTypename<
  OrganizationUsersQuery["organizationUsers"],
  "QueryOrganizationUsersSuccess"
>["data"]["users"];

const noUsers: OrganizationUsersResponse = [];

export const ExistingEmployeeSelect = ({
  placeholder,
  onChange,
  defaultValue,
  disabled,
  exceptUserId,
}: Props) => {
  const [searchText, setSearchText] = useState("");
  const { data, loading } = useOrganizationUsersQuery({
    variables: {
      offset: 0,
      pageSize: 3,
      name: searchText || null,
      exceptUserIds: exceptUserId ? [exceptUserId] : undefined,
    },
  });

  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Employee | undefined>({
    workEmail: "",
    name: defaultValue?.displayName || "",
    userId: "",
    employeeProfileId: "",
  });

  let users = noUsers;
  if (data?.organizationUsers?.__typename === "QueryOrganizationUsersSuccess") {
    users = data.organizationUsers.data.users;
  }

  let triggerIcon: React.ReactNode = selected?.name ? (
    <Plus className="ml-2 size-4 shrink-0 opacity-50" />
  ) : (
    <X
      className="ml-2 size-4 shrink-0 opacity-50"
      onClick={() => setSelected(undefined)}
    />
  );

  if (disabled) {
    triggerIcon = null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between border-none bg-[#1c1c1c]",
            selected?.name ? "text-white" : "text-[#a1a1aa]",
          )}
        >
          {selected?.name || placeholder}
          {triggerIcon}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[335px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            onValueChange={setSearchText}
            value={searchText}
            placeholder="Search employees"
          />
          <CommandEmpty>
            <Body size="md" weight="regular">
              {loading ? "Searching" : "No results found"}
            </Body>
          </CommandEmpty>
          <CommandGroup>
            {searchText.length < 3 && users.length > 0 && (
              <Body size="sm" weight="regular" className="p-3 text-center">
                Recommended users
              </Body>
            )}
            {users.map((user) => (
              <CommandItem
                key={user.userId}
                value={user.userId}
                onSelect={() => {
                  if (selected?.userId === user.userId) {
                    setSelected(undefined);
                  } else {
                    const e: Employee = {
                      name: user.displayName,
                      workEmail: user.employee!.workEmail,
                      userId: user.userId,
                      employeeProfileId: user.employee!.employeeId,
                    };
                    setSelected(e);
                    onChange(e);
                  }
                  setOpen(false);
                }}
              >
                <div className="flex w-full justify-between">
                  <div className="flex flex-row">
                    <Avatar>
                      <AvatarFallback>
                        {getInitialsFromDisplayName(user.displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col pl-2">
                      <Body size="md" weight="regular">
                        {user.displayName}
                      </Body>
                      <Body size="md" weight="regular">
                        {user.employee?.workEmail}
                      </Body>
                    </div>
                  </div>
                  <div className="flex-end flex items-center justify-end">
                    <Check
                      className={cn(
                        "w-100 mr-2 size-4",
                        selected?.workEmail === user.employee?.workEmail
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
